import type { CreateItemRequest, Locale, ParsedItem, PlanDay, Trip } from '@itin/shared'
import { canonicalZone, wallClockToInstant, zoneCityLabel } from '@itin/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from '../lib/api.js'
import { formatDate, useLocale, useT } from '../lib/i18n.js'
import { KIND_GLYPH } from './ItemForm.js'
import { Button, Card, ErrorNotice, Field, TextArea } from './ui.js'

/**
 * Pasting a confirmation email instead of retyping it.
 *
 * The biggest single piece of typing Ana does per booking, and the one where a
 * transposed booking reference goes unnoticed. So the shape of this screen is
 * read → **look** → add: what comes back is a draft, every field it read is on
 * screen next to the label it will be saved under, and nothing reaches the
 * itinerary until she presses Add. A confirmation number she never saw is worse
 * than one she retyped, which is the whole reason there is a confirmation step
 * rather than a save.
 *
 * The conversion from what the email printed to what gets stored happens here,
 * in the client, through `wallClockToInstant` — the same one converter the item
 * form uses, for the reason PATTERNS §3b gives. The server deliberately hands
 * back `'07:05'` and a zone rather than an instant, so nothing between the
 * email and this line has silently guessed which 07:05 was meant.
 */
export function ConfirmationImport({
  trip,
  mode = 'confirmation',
}: {
  trip: Trip
  /** Notes are Ana's own shorthand; same draft-and-confirm flow, different brief. */
  mode?: 'confirmation' | 'notes'
}) {
  const t = useT()
  /** Message-key family. Only the wording that differs moves; the rest is shared. */
  const k = mode === 'notes' ? 'notes' : 'confirmations'
  const queryClient = useQueryClient()

  const [text, setText] = useState('')
  /** Null before a read, and again once a draft is added or discarded. */
  const [drafts, setDrafts] = useState<Array<{ item: ParsedItem; include: boolean }> | null>(null)
  const [notice, setNotice] = useState<'unavailable' | 'failed' | 'nothingFound' | null>(null)

  // The same query key the plan editor uses, so this costs no extra request:
  // the days are needed to file each booking under the date it happens on.
  const plan = useQuery({ queryKey: ['plan', trip.id], queryFn: () => api.getPlan(trip.id) })

  const read = useMutation({
    mutationFn: () => api.parseConfirmation(trip.id, { text, mode }),
    onSuccess(result) {
      if (result.status !== 'read') {
        setDrafts(null)
        setNotice(result.status)
        return
      }
      // An empty list is a real answer — she pasted a newsletter — and it reads
      // differently from the reader being absent or broken.
      setDrafts(result.items.map((item) => ({ item, include: true })))
      setNotice(result.items.length === 0 ? 'nothingFound' : null)
    },
  })

  const chosen = (drafts ?? []).filter((draft) => draft.include).map((draft) => draft.item)

  const add = useMutation({
    mutationFn: async () => {
      // Sequentially, not in parallel: each create geocodes its address biased
      // towards the pins already on the trip, and firing six at once means six
      // lookups that cannot see each other's results.
      for (const item of chosen) {
        await api.createItem(trip.id, toCreateItem(item, trip, plan.data?.days ?? []))
      }
    },
    async onSuccess() {
      await queryClient.invalidateQueries({ queryKey: ['plan', trip.id] })
      setDrafts(null)
      setNotice(null)
      setText('')
    },
  })

  return (
    <Card
      testId={mode === 'notes' ? 'notes-import' : 'confirmation-import'}
      title={t(`${k}.title`)}
      collapsible
    >
      <div className="flex flex-col gap-4">
        <p className="text-xs text-slate-500">{t(`${k}.hint`)}</p>

        <Field label={t(`${k}.field.text`)} guidance={`${k}.field.text.guidance`}>
          <TextArea
            data-testid={mode === 'notes' ? 'notes-text' : 'confirmation-text'}
            rows={5}
            value={text}
            placeholder={t(`${k}.placeholder`)}
            onChange={(event) => setText(event.target.value)}
          />
        </Field>

        <div>
          <Button
            variant="quiet"
            disabled={read.isPending || text.trim() === ''}
            onClick={() => {
              setNotice(null)
              read.mutate()
            }}
          >
            {read.isPending ? t('confirmations.reading') : t('confirmations.read')}
          </Button>
        </div>

        {read.isError && <ErrorNotice error={read.error} />}
        {add.isError && <ErrorNotice error={add.error} />}

        {notice && (
          <p
            role="status"
            data-testid={`${mode === 'notes' ? 'notes' : 'confirmation'}-${notice}`}
            className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"
          >
            {t(`${k}.${notice}`)}
          </p>
        )}

        {drafts && drafts.length > 0 && (
          <div className="flex flex-col gap-3" data-testid={mode === 'notes' ? 'notes-drafts' : 'confirmation-drafts'}>
            <p className="text-sm font-medium text-slate-700">
              {drafts.length === 1
                ? t(`${k}.foundOne`)
                : t(`${k}.found`, { count: drafts.length })}
            </p>
            {/*
              Said here rather than only in the card's introduction. This is the
              sentence that has to be on screen at the moment she is deciding,
              because the field it is about is the one she cannot check later.
            */}
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {t(`${k}.checkHint`)}
            </p>

            {drafts.map((draft, index) => (
              <DraftCard
                key={index}
                parsed={draft.item}
                include={draft.include}
                trip={trip}
                mode={mode}
                days={plan.data?.days ?? []}
                onToggle={() =>
                  setDrafts((current) =>
                    (current ?? []).map((entry, at) =>
                      at === index ? { ...entry, include: !entry.include } : entry,
                    ),
                  )
                }
              />
            ))}

            <div className="flex flex-wrap gap-2">
              <Button
                variant="primary"
                disabled={add.isPending || chosen.length === 0}
                onClick={() => add.mutate()}
              >
                {add.isPending
                  ? t('confirmations.adding')
                  : chosen.length === 1
                    ? t('confirmations.addOne')
                    : t('confirmations.add', { count: chosen.length })}
              </Button>
              <Button
                variant="quiet"
                disabled={add.isPending}
                onClick={() => {
                  setDrafts(null)
                  setNotice(null)
                }}
              >
                {t('confirmations.discard')}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  )
}

/**
 * One booking, with every field it read shown under the label it will be saved
 * under. Read-only on purpose: the ordinary item form is one click away once it
 * is on the day, and a second editable copy of eight fields is two forms that
 * drift apart. What this has to do is let her *see*, not let her type.
 */
function DraftCard({
  parsed,
  include,
  trip,
  mode,
  days,
  onToggle,
}: {
  parsed: ParsedItem
  include: boolean
  trip: Trip
  mode: 'confirmation' | 'notes'
  days: PlanDay[]
  onToggle(): void
}) {
  const t = useT()
  const { locale } = useLocale()

  const startZone = parsed.timezone ?? trip.timezone
  const endZone = parsed.endTimezone ?? startZone
  const landsOnADay = parsed.startDate !== null && days.some((day) => day.date === parsed.startDate)

  return (
    <div className="flex flex-col gap-2 rounded-md border border-slate-200 bg-[#fdfcf9] p-3">
      <label className="flex items-start gap-2">
        <input type="checkbox" checked={include} onChange={onToggle} className="mt-1.5" />
        <span className="flex min-w-0 flex-col">
          <span className="text-xs uppercase tracking-wide text-slate-500">
            <span aria-hidden="true" className="mr-1">
              {KIND_GLYPH[parsed.kind]}
            </span>
            {t(`item.kind.${parsed.kind}`)}
          </span>
          <span className="font-medium text-slate-900">{parsed.title}</span>
        </span>
      </label>

      <dl className="grid grid-cols-1 gap-1 pl-6 text-sm text-slate-600">
        <Detail
          label={t('confirmations.when')}
          value={reading(parsed.startDate, parsed.startTime, locale)}
          note={
            parsed.timezone
              ? t('confirmations.onClockIn', { city: zoneCityLabel(startZone, locale) })
              : undefined
          }
        />
        <Detail
          label={t('confirmations.ends')}
          value={reading(parsed.endDate, parsed.endTime, locale)}
          note={
            parsed.endTimezone
              ? t('confirmations.onClockIn', { city: zoneCityLabel(endZone, locale) })
              : undefined
          }
        />
        <Detail label={t('item.field.address')} value={parsed.address} />
        <Detail label={t('item.field.phone')} value={parsed.phone} />
        <Detail label={t('item.field.website')} value={parsed.website} />
        {/* Plain text, never a link — a booking reference that reads like a
            phone number became a dialable one once already. PATTERNS §6. */}
        <Detail label={t('item.field.confirmationRef')} value={parsed.confirmationRef} />
        <Detail label={t('item.field.notes')} value={parsed.notes} />
      </dl>

      {/*
        The absences worth stating. A booking reference that was not found looks
        identical to one that was not looked for, and the difference decides
        whether she goes back to the email.
      */}
      {mode === 'confirmation' && parsed.confirmationRef === null && (
        <p className="pl-6 text-xs text-slate-500">{t('confirmations.noReference')}</p>
      )}
      {!landsOnADay && (
        <p className="pl-6 text-xs text-slate-500">
          {parsed.startDate === null
            ? t(mode === 'notes' ? 'notes.noDate' : 'confirmations.noDate')
            : t(mode === 'notes' ? 'notes.outsideTrip' : 'confirmations.outsideTrip')}
        </p>
      )}
    </div>
  )
}

function Detail({
  label,
  value,
  note,
}: {
  label: string
  value: string | null
  note?: string | undefined
}) {
  if (value === null) return null
  return (
    <div>
      <dt className="inline text-slate-400">{label}: </dt>
      <dd className="inline whitespace-pre-line">
        {value}
        {note ? <span className="text-slate-400"> — {note}</span> : null}
      </dd>
    </div>
  )
}

/** The date and clock reading as the email printed them, in her locale. */
function reading(date: string | null, time: string | null, locale: Locale): string | null {
  const parts: string[] = []
  if (date) parts.push(formatDate(date, locale))
  if (time) parts.push(time)
  return parts.length > 0 ? parts.join(' · ') : null
}

/** Zones agree when the runtime says they do, never by string equality. §3b. */
function sameZone(a: string, b: string): boolean {
  return (canonicalZone(a) ?? a) === (canonicalZone(b) ?? b)
}

/**
 * A draft, as the create-an-item route wants it.
 *
 * Two rules are copied from the item form rather than reinvented, because the
 * two paths have to produce identical rows. A zone is only written down when it
 * differs from the trip's — writing the trip's zone onto every entry would make
 * `timezone ?? trip.timezone` mean nothing — and the same for an end zone
 * against the start's.
 */
export function toCreateItem(parsed: ParsedItem, trip: Trip, days: PlanDay[]): CreateItemRequest {
  const startZone = parsed.timezone ?? trip.timezone
  const endZone = parsed.endTimezone ?? startZone
  // The server already rolled an arrival past midnight onto the next date, so
  // an end with no date of its own is genuinely on the start's.
  const endDate = parsed.endDate ?? parsed.startDate

  const startsAt =
    parsed.startDate && parsed.startTime
      ? wallClockToInstant(`${parsed.startDate}T${parsed.startTime}`, startZone)
      : null
  const endsAt =
    endDate && parsed.endTime ? wallClockToInstant(`${endDate}T${parsed.endTime}`, endZone) : null

  const day = parsed.startDate ? days.find((entry) => entry.date === parsed.startDate) : undefined

  return {
    kind: parsed.kind,
    title: parsed.title,
    ...(startsAt ? { startsAt: startsAt.toISOString() } : {}),
    ...(endsAt ? { endsAt: endsAt.toISOString() } : {}),
    ...(parsed.timezone && !sameZone(parsed.timezone, trip.timezone)
      ? { timezone: parsed.timezone }
      : {}),
    ...(parsed.endTimezone && !sameZone(parsed.endTimezone, startZone)
      ? { endTimezone: parsed.endTimezone }
      : {}),
    ...(parsed.address ? { address: parsed.address } : {}),
    ...(parsed.phone ? { phone: parsed.phone } : {}),
    ...(parsed.website ? { website: parsed.website } : {}),
    ...(parsed.confirmationRef ? { confirmationRef: parsed.confirmationRef } : {}),
    ...(parsed.notes ? { notes: parsed.notes } : {}),
    // Outside the trip's dates it lands unscheduled rather than being dropped —
    // surfaced, not filtered. PATTERNS §6.
    ...(day ? { dayId: day.id } : {}),
  }
}
